"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IdentitySchemas = exports.WebhookSchemas = exports.PaymentSchemas = exports.ProjectSchemas = exports.MembershipSchemas = exports.TemplateSchemas = exports.ContactSchemas = exports.ActionSchemas = exports.CampaignSchemas = exports.EventSchemas = exports.UserSchemas = exports.UtilitySchemas = exports.PRICING = exports.PLANS = void 0;
const zod_1 = require("zod");
const client_1 = require("@prisma/client");
exports.PLANS = {
    FREE: 0,
    CONSUMPTION: 1,
};
exports.PRICING = [
    {
        id: 'FREE',
        plan: 'Free',
        description: 'Perfect for bootstrappers',
        automation: 1000,
        transactional: 1000,
        campaign: 1000,
        email_tracking: false,
        custom_email: true,
        support: false,
        seats: 1,
        monthly_price: '0.00',
        monthly_integer_price: 0,
        yearly_price: '0.00',
        yearly_integer_price: 0,
        show: true,
    },
    {
        id: 'CONSUMPTION',
        plan: 'Consumption',
        description: 'Pay for what you send',
        automation: Infinity,
        transactional: Infinity,
        campaign: Infinity,
        seats: 100,
        email_tracking: true,
        custom_email: true,
        support: true,
        unit_price: '0.001',
        show: true,
    },
];
const email = zod_1.z
    .string({ invalid_type_error: 'Email needs to be a string', required_error: 'Email is required' })
    .email({ message: 'Invalid email address' })
    .transform(e => e.toLowerCase());
const password = zod_1.z.string().min(6, 'Password needs to be at least 6 characters long');
const id = zod_1.z
    .string({ invalid_type_error: 'ID needs to be a string', required_error: 'ID is required' })
    .uuid({ message: 'Id needs to be a valid UUID' });
exports.UtilitySchemas = {
    id: zod_1.z.object({
        id,
    }),
    email: zod_1.z.object({
        email,
    }),
    pagination: zod_1.z.object({
        page: zod_1.z
            .number({
            invalid_type_error: 'Page needs to be a number',
            required_error: 'Page is required',
        })
            .min(1, 'Page needs to be at least 1')
            .default(1)
            .or(zod_1.z.string().transform(s => {
            return Number(s);
        })),
    }),
    convert: zod_1.z.object({
        markdown: zod_1.z.string({
            invalid_type_error: 'Markdown needs to be a string',
            required_error: 'Markdown is required',
        }),
    }),
};
exports.UserSchemas = {
    credentials: zod_1.z.object({
        email: email,
        password: password,
    }),
};
const zodSchema = zod_1.z.record(zod_1.z.union([
    zod_1.z
        .string({
        invalid_type_error: 'Metadata can only be a string, array of strings or a non-persistent object (https://docs.useplunk.com/working-with-contacts/metadata#non-persistent-metadata)',
    })
        .transform(s => {
        return { persistent: true, value: s };
    }),
    zod_1.z
        .array(zod_1.z.string({
        invalid_type_error: 'Metadata can only be a string, array of strings or a non-persistent object (https://docs.useplunk.com/working-with-contacts/metadata#non-persistent-metadata)',
    }))
        .transform(s => {
        return { persistent: true, value: s };
    }),
    zod_1.z.object({
        persistent: zod_1.z.boolean({ invalid_type_error: 'Persistent should be a boolean' }).optional().default(true),
        value: zod_1.z.union([zod_1.z.string(), zod_1.z.array(zod_1.z.string())], {
            invalid_type_error: 'Metadata can only be a string, array of strings or a non-persistent object (https://docs.useplunk.com/working-with-contacts/metadata#non-persistent-metadata)',
        }),
    }, {
        invalid_type_error: 'Metadata can only be a string, array of strings or a non-persistent object (https://docs.useplunk.com/working-with-contacts/metadata#non-persistent-metadata)',
    }),
], {
    invalid_type_error: 'Metadata can only be a string, array of strings or a non-persistent object (https://docs.useplunk.com/working-with-contacts/metadata#non-persistent-metadata)',
}), { invalid_type_error: 'Metadata should be an object (https://docs.useplunk.com/working-with-contacts/metadata)' });
exports.EventSchemas = {
    post: zod_1.z.object({
        email,
        subscribed: zod_1.z
            .boolean({
            invalid_type_error: 'Subscribed should be a boolean. Read more: https://docs.useplunk.com/api-reference/actions/track',
        })
            .nullish(),
        event: zod_1.z
            .string({
            required_error: 'Event is required. Read more: https://docs.useplunk.com/api-reference/actions/track',
            invalid_type_error: 'Event can only be a string. Read more: https://docs.useplunk.com/api-reference/actions/track',
        })
            .transform(n => n.toLowerCase())
            .transform(n => n.replace(/ /g, '-')),
        data: zodSchema.nullish(),
    }),
    send: zod_1.z.object({
        subscribed: zod_1.z.boolean({ invalid_type_error: 'Subscribed should be a boolean' }).nullish(),
        from: email.nullish(),
        name: zod_1.z.string().nullish(),
        reply: email.nullish(),
        type: zod_1.z.enum(['html', 'markdown']).default('html'),
        to: zod_1.z
            .array(email)
            .max(5, 'You can only send transactional emails to 5 people at a time')
            .or(email.transform(e => [e])),
        subject: zod_1.z.string({
            required_error: 'Subject is required. Read more: https://docs.useplunk.com/api-reference/transactional/send',
        }),
        body: zod_1.z.string({
            required_error: 'Body is required. Read more: https://docs.useplunk.com/api-reference/transactional/send',
        }),
        headers: zod_1.z.record(zod_1.z.string()).nullish(),
    }),
};
exports.CampaignSchemas = {
    send: zod_1.z.object({
        id,
        live: zod_1.z.boolean().default(false),
        delay: zod_1.z.number().int('Delay needs to be a whole number').nonnegative('Delay needs to be a positive number'),
    }),
    create: zod_1.z.object({
        subject: zod_1.z
            .string()
            .min(1, 'Subject needs to be at least 1 character long')
            .max(70, 'Subject needs to be less than 70 characters long'),
        body: zod_1.z.string().min(1, 'Body needs to be at least 1 character long'),
        email: email.nullish().or(zod_1.z.literal('')),
        from: zod_1.z.string().nullish(),
        recipients: zod_1.z.array(zod_1.z.string()),
        style: zod_1.z.nativeEnum(client_1.TemplateStyle).default('SANS'),
    }),
    update: zod_1.z.object({
        id,
        subject: zod_1.z
            .string()
            .min(1, 'Subject needs to be at least 1 character long')
            .max(70, 'Subject needs to be less than 70 characters long'),
        body: zod_1.z.string().min(1, 'Body needs to be at least 1 character long'),
        email: email.nullish().or(zod_1.z.literal('')),
        from: zod_1.z.string().nullish(),
        recipients: zod_1.z.array(zod_1.z.string()),
        style: zod_1.z.nativeEnum(client_1.TemplateStyle).default('SANS'),
    }),
};
exports.ActionSchemas = {
    create: zod_1.z.object({
        name: zod_1.z.string().min(1, 'Name needs to be at least 1 character long'),
        runOnce: zod_1.z.boolean().default(false),
        delay: zod_1.z.number().int('Delay needs to be a whole number').nonnegative('Delay needs to be a positive number'),
        template: id,
        events: zod_1.z.array(id).min(1, 'Select at least one event'),
        notevents: zod_1.z.array(id).optional().default([]),
    }),
    update: zod_1.z.object({
        id,
        name: zod_1.z.string().min(1, 'Name needs to be at least 1 character long'),
        runOnce: zod_1.z.boolean().default(false),
        delay: zod_1.z.number().int('Delay needs to be a whole number').nonnegative('Delay needs to be a positive number'),
        template: id,
        events: zod_1.z.array(id).default([]),
        notevents: zod_1.z.array(id).optional().default([]),
    }),
};
exports.ContactSchemas = {
    create: zod_1.z.object({
        email,
        data: zod_1.z
            .object({})
            .catchall(zod_1.z.union([zod_1.z.string(), zod_1.z.array(zod_1.z.string())]))
            .or(zod_1.z.string().transform(s => (s === '' ? null : JSON.parse(s))))
            .nullish(),
        subscribed: zod_1.z.boolean(),
    }),
    manage: zod_1.z
        .object({
        id: id.optional(),
        email: email.optional(),
        data: zod_1.z
            .object({})
            .catchall(zod_1.z.union([zod_1.z.string(), zod_1.z.array(zod_1.z.string()), zod_1.z.null()]))
            .or(zod_1.z.string().transform(s => (s === '' ? null : JSON.parse(s))))
            .nullish(),
        subscribed: zod_1.z.boolean().nullish(),
    })
        .refine(data => {
        return data.id || data.email;
    }, { message: 'Either id or email should be specified' })
        .refine(data => {
        // if id and email are both present
        return !(data.id && data.email);
    }, { message: 'Either id or email should be specified' }),
};
exports.TemplateSchemas = {
    create: zod_1.z.object({
        subject: zod_1.z.string().min(1, "Subject can't be empty").max(70, 'Subject needs to be less than 70 characters long'),
        body: zod_1.z.string().min(1, "Body can't be empty"),
        email: email.nullish().or(zod_1.z.literal('')),
        from: zod_1.z.string().nullish(),
        type: zod_1.z.nativeEnum(client_1.TemplateType).default('MARKETING'),
        style: zod_1.z.nativeEnum(client_1.TemplateStyle).default('SANS'),
    }),
    update: zod_1.z.object({
        id,
        subject: zod_1.z.string().min(1, "Subject can't be empty").max(70, 'Subject needs to be less than 70 characters long'),
        body: zod_1.z.string().min(1, "Body can't be empty"),
        email: email.nullish().or(zod_1.z.literal('')),
        from: zod_1.z.string().nullish(),
        type: zod_1.z.nativeEnum(client_1.TemplateType).default('MARKETING'),
        style: zod_1.z.nativeEnum(client_1.TemplateStyle).default('SANS'),
    }),
};
exports.MembershipSchemas = {
    invite: zod_1.z.object({
        id,
        email,
        role: zod_1.z.enum(['MEMBER', 'ADMIN']).default('MEMBER'),
    }),
    kick: zod_1.z.object({
        id,
        email,
    }),
};
exports.ProjectSchemas = {
    secret: zod_1.z.object({
        secret: zod_1.z.string(),
    }),
    create: zod_1.z.object({
        name: zod_1.z.string().min(1, "Name can't be empty"),
        url: zod_1.z
            .string()
            .regex(/^(?:(?:https?):\/\/)?(?:[\w-]+\.)+[a-z]{2,}(?:\/[^\s]*)?$/)
            .transform(u => (u.startsWith('http') ? u : `https://${u}`)),
    }),
    update: zod_1.z.object({
        id: id,
        name: zod_1.z.string().min(1, "Name can't be empty"),
        tracking: zod_1.z.boolean(),
        url: zod_1.z
            .string()
            .regex(/^(?:(?:https?):\/\/)?(?:[\w-]+\.)+[a-z]{2,}(?:\/[^\s]*)?$/)
            .transform(u => (u.startsWith('http') ? u : `https://${u}`)),
    }),
    analytics: zod_1.z.object({
        method: zod_1.z.enum(['week', 'month', 'year']).default('week'),
    }),
};
exports.PaymentSchemas = {
    payment: zod_1.z.object({
        id: id,
        subscription: zod_1.z.enum(['CONSUMPTION']),
    }),
    session: zod_1.z.object({
        id: zod_1.z.string(),
    }),
};
exports.WebhookSchemas = {
    create: zod_1.z.object({
        url: zod_1.z
            .string()
            .regex(/^(?:(?:https?):\/\/)?(?:[\w-]+\.)+[a-z]{2,}(?:\/[^\s]*)?$/)
            .transform(u => (u.startsWith('http') ? u : `https://${u}`)),
        events: zod_1.z.array(zod_1.z.nativeEnum(client_1.WebhookEvents)).min(1, 'Select at least one event'),
    }),
    update: zod_1.z.object({
        id: id,
        url: zod_1.z
            .string()
            .regex(/^(?:(?:https?):\/\/)?(?:[\w-]+\.)+[a-z]{2,}(?:\/[^\s]*)?$/)
            .transform(u => (u.startsWith('http') ? u : `https://${u}`)),
        events: zod_1.z.array(zod_1.z.nativeEnum(client_1.WebhookEvents)).min(1, 'Select at least one event'),
    }),
};
exports.IdentitySchemas = {
    create: zod_1.z.object({
        id: id,
        email: email.refine(e => {
            return ![
                'gmail.com',
                'outlook.com',
                'hotmail.com',
                'yahoo.com',
                'useplunk.com',
                'useplunk.dev',
                'useplunk.net',
            ].includes(e.split('@')[1]);
        }, { message: 'Please use your own domain' }),
    }),
    update: zod_1.z.object({
        id: id,
        from: zod_1.z.string().min(1, "Name can't be empty"),
    }),
};
